import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  MoreHorizontal,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Mail,
  KeyRound,
  UserPlus,
  Download,
  Upload,
  FileSpreadsheet,
  Trash2,
  ArrowRight,
  Users as UsersIcon,
  Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader, StatStrip, StatusPill, Segmented } from '@/components/page';
import { EmptyState, Notice, ago, fmtDate, rowLink } from '@/components/workspace-ui';
import { ConfirmDialog, SecretDialog, UserAvatar } from '@/components/admin-ui';
import {
  useUsersPage,
  useUserStats,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useImportUsers,
  useResendVerification,
  useOrganizations,
  useToast,
  useAuth,
  useDebouncedValue,
} from '@/hooks';
import { cn } from '@/lib/utils';
import { usersApi } from '@/api/users';
import type { AdminUser, UserImportMode, UserImportResult, UsersListQuery } from '@/api/users';

type Seg = 'all' | 'active' | 'disabled' | 'unverified' | 'super';

const SEG_FILTER: Record<Seg, Partial<UsersListQuery>> = {
  all: {},
  active: { isActive: true },
  disabled: { isActive: false },
  unverified: { emailVerified: false },
  super: { isSuperAdmin: true },
};

const PAGE_SIZE = 10;
const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;

type PendingAction =
  | { kind: 'disable' | 'enable' | 'delete' | 'toggleAdmin' | 'resetPassword'; user: AdminUser }
  | null;

function tempPassword() {
  return (
    'Tmp-' +
    Array.from(crypto.getRandomValues(new Uint8Array(6)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  );
}

export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const seg: Seg = (params.get('seg') as Seg) in SEG_FILTER ? (params.get('seg') as Seg) : 'all';
  const org = params.get('org') ?? '';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [page, setPage] = useState(1);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);
  const [secret, setSecret] = useState<{ email: string; password: string; created?: boolean } | null>(
    null
  );

  const debouncedSearch = useDebouncedValue(search);
  const { data: organizations = [] } = useOrganizations();
  const { data: stats } = useUserStats();
  const {
    data: result,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useUsersPage({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    organizationId: org || undefined,
    ...SEG_FILTER[seg],
  });
  const users = result?.data ?? [];
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const resend = useResendVerification();

  const setQuery = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v && !(k === 'seg' && v === 'all')) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
    setPage(1);
  };

  const filtersActive = !!(search || org || seg !== 'all');
  const clearFilters = () => {
    setSearch('');
    setParams(new URLSearchParams(), { replace: true });
    setPage(1);
  };

  const counts: Record<Seg, number | undefined> = {
    all: stats?.total,
    active: stats?.active,
    disabled: stats?.disabled,
    unverified: stats?.unverified,
    super: stats?.superAdmins,
  };

  const fail = (title: string, e: unknown) =>
    toast({
      variant: 'destructive',
      title,
      description: e instanceof Error ? e.message : 'Something went wrong',
    });

  const handleResend = async (u: AdminUser) => {
    try {
      await resend.mutateAsync(u.id);
      toast({ title: 'Verification email sent', description: u.email });
    } catch (e) {
      fail('Could not send verification email', e);
    }
  };

  const confirmProps = (() => {
    if (!pending) return null;
    const u = pending.user;
    const name = u.name || u.email;
    switch (pending.kind) {
      case 'disable':
        return {
          title: `Disable ${name}?`,
          body: (
            <>
              <span className="font-mono">{u.email}</span> is signed out of every session and can’t
              sign in until re-enabled. App grants are kept.
            </>
          ),
          confirmLabel: 'Disable account',
          danger: true,
        };
      case 'enable':
        return {
          title: `Enable ${name}?`,
          body: (
            <>
              <span className="font-mono">{u.email}</span> will be able to sign in again. App grants
              are unchanged.
            </>
          ),
          confirmLabel: 'Enable account',
        };
      case 'toggleAdmin':
        return u.isSuperAdmin
          ? {
              title: `Remove super admin from ${name}?`,
              body: 'They keep their organization role and app grants but lose every admin page immediately.',
              confirmLabel: 'Remove super admin',
            }
          : {
              title: `Make ${name} a super admin?`,
              body: 'Super admins can change any organization, app, OAuth client and signing key, read the full audit log, and disable other users. Grant this only to people who operate authrz.me itself.',
              confirmLabel: 'Make super admin',
              danger: true,
            };
      case 'resetPassword':
        return {
          title: `Set a temporary password for ${name}?`,
          body: 'A new temporary password replaces their current one and is shown once. Share it securely; they should change it after signing in.',
          confirmLabel: 'Set temporary password',
          danger: true,
        };
      case 'delete':
        return {
          title: `Delete ${name}?`,
          body: 'This permanently removes the account, every app grant and their organization membership. Audit events are kept. This can’t be undone.',
          confirmLabel: 'Delete user',
          danger: true,
          typeToConfirm: u.email,
        };
    }
  })();

  const runPending = async () => {
    if (!pending) return;
    const u = pending.user;
    try {
      switch (pending.kind) {
        case 'delete':
          await deleteUser.mutateAsync(u.id);
          toast({ title: 'User deleted', description: u.email });
          break;
        case 'toggleAdmin':
          await updateUser.mutateAsync({ id: u.id, data: { isSuperAdmin: !u.isSuperAdmin } });
          toast({
            title: u.isSuperAdmin ? 'Super admin removed' : 'Super admin granted',
            description: u.email,
          });
          break;
        case 'disable':
        case 'enable':
          await updateUser.mutateAsync({ id: u.id, data: { isActive: pending.kind === 'enable' } });
          toast({
            title: pending.kind === 'enable' ? 'Account enabled' : 'Account disabled',
            description: u.email,
          });
          break;
        case 'resetPassword': {
          const temp = tempPassword();
          await updateUser.mutateAsync({ id: u.id, data: { password: temp } });
          setSecret({ email: u.email, password: temp });
          break;
        }
      }
    } catch (e) {
      fail('Action failed', e);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      downloadBlob(await usersApi.downloadImportTemplate(), 'authrz-users-import-template.csv');
    } catch (e) {
      fail('Download failed', e);
    }
  };

  const handleExportUsers = async () => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(await usersApi.exportCsv(), `authrz-users-${stamp}.csv`);
    } catch (e) {
      fail('Export failed', e);
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        description="Every authrz.me account across all organizations. Open a user to manage their organization, app access and sessions."
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <FileSpreadsheet />
                  CSV
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setImportOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import users…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportUsers}>
                  <Download className="mr-2 h-4 w-4" />
                  Export users
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDownloadTemplate}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Download template
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus />
              Invite user
            </Button>
          </>
        }
      />

      <div className="space-y-4">
        <StatStrip
          items={[
            { label: 'Users', value: stats?.total ?? '—', note: 'All accounts' },
            { label: 'Active', value: stats?.active ?? '—', note: 'Can sign in' },
            { label: 'Unverified', value: stats?.unverified ?? '—', note: 'Email not confirmed' },
            { label: 'Super admins', value: stats?.superAdmins ?? '—', note: 'Platform-wide access' },
          ]}
        />

        <Card aria-label="All users" className="overflow-hidden">
          <div className="flex flex-col gap-2 border-b p-3 lg:flex-row lg:flex-wrap lg:items-center">
            <div className="relative w-full lg:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                aria-label="Search users"
                placeholder="Search name, email or ID"
                autoComplete="off"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setQuery({ q: e.target.value });
                }}
                className="pl-9"
              />
            </div>
            <div className="max-w-full overflow-x-auto">
              <Segmented<Seg>
                value={seg}
                onChange={(v) => setQuery({ seg: v })}
                options={[
                  { value: 'all', label: 'All', count: counts.all },
                  { value: 'active', label: 'Active', count: counts.active },
                  { value: 'disabled', label: 'Disabled', count: counts.disabled },
                  { value: 'unverified', label: 'Unverified', count: counts.unverified },
                  { value: 'super', label: 'Super admins', count: counts.super },
                ]}
              />
            </div>
            <Select
              value={org || 'all'}
              onValueChange={(v) => setQuery({ org: v === 'all' ? '' : v })}
            >
              <SelectTrigger aria-label="Organization" className="w-full lg:ml-auto lg:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                <SelectItem value="none">No organization</SelectItem>
                {organizations.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isError ? (
            <div className="p-4">
              <Notice
                tone="bad"
                title="Couldn’t load users"
                action={
                  <Button size="sm" variant="outline" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                {error instanceof Error ? error.message : 'The request failed.'}
              </Notice>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={cn(isFetching && !isLoading && 'opacity-70 transition-opacity')}>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : users.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="p-0">
                      {filtersActive ? (
                        <EmptyState
                          icon={Search}
                          title="No users match"
                          body="Try a different name or email, or clear the filters."
                          action={
                            <Button variant="outline" onClick={clearFilters}>
                              Clear filters
                            </Button>
                          }
                        />
                      ) : (
                        <EmptyState
                          icon={UsersIcon}
                          title="No users yet"
                          body="Invite the first person to your authrz.me workspace."
                          action={
                            <Button onClick={() => setInviteOpen(true)}>
                              <UserPlus />
                              Invite user
                            </Button>
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => {
                    const self = u.id === currentUser?.id;
                    return (
                      <TableRow
                        key={u.id}
                        className={cn(rowLink, !u.isActive && 'opacity-60')}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('a,button,[role=menuitem]'))
                            navigate(`/users/${u.id}`);
                        }}
                      >
                        <TableCell>
                          <Link to={`/users/${u.id}`} className="flex min-w-0 items-center gap-3">
                            <UserAvatar name={u.name} email={u.email} picture={u.picture} />
                            <span className="min-w-0">
                              <b
                                className="block max-w-[220px] truncate font-medium"
                                title={u.name ?? ''}
                              >
                                {u.name || 'Unnamed user'}
                                {self && (
                                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                                    (you)
                                  </span>
                                )}
                              </b>
                              <span
                                className="block max-w-[220px] truncate font-mono text-xs text-muted-foreground"
                                title={u.email}
                              >
                                {u.email}
                              </span>
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          {u.organization ? (
                            <span className="block min-w-0">
                              <Link
                                to={`/organizations/${u.organization.id}`}
                                className="block max-w-[180px] truncate hover:underline"
                                title={u.organization.name}
                              >
                                {u.organization.name}
                              </Link>
                              <span className="block font-mono text-xs text-muted-foreground">
                                {u.organization.role}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No organization</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusPill tone={u.emailVerified ? 'ok' : 'warn'}>
                            {u.emailVerified ? 'Verified' : 'Unverified'}
                          </StatusPill>
                        </TableCell>
                        <TableCell>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <StatusPill tone={u.isActive ? 'ok' : 'neutral'}>
                              {u.isActive ? 'Active' : 'Disabled'}
                            </StatusPill>
                            {u.isSuperAdmin && <Badge variant="outline">super admin</Badge>}
                          </span>
                        </TableCell>
                        <TableCell
                          className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground"
                          title={u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'Never signed in'}
                        >
                          {u.lastLoginAt ? ago(u.lastLoginAt) : 'Never'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                          {fmtDate(u.createdAt)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${u.name || u.email}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/users/${u.id}`}>
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  Open user
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={u.emailVerified || resend.isPending}
                                onClick={() => handleResend(u)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                Resend verification email
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setPending({ kind: 'resetPassword', user: u })}
                              >
                                <KeyRound className="mr-2 h-4 w-4" />
                                Set temporary password
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={self}
                                onClick={() => setPending({ kind: 'toggleAdmin', user: u })}
                              >
                                {u.isSuperAdmin ? (
                                  <ShieldOff className="mr-2 h-4 w-4" />
                                ) : (
                                  <Shield className="mr-2 h-4 w-4" />
                                )}
                                {u.isSuperAdmin ? 'Remove super admin' : 'Make super admin'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {u.isActive ? (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={self}
                                  onClick={() => setPending({ kind: 'disable', user: u })}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  {self ? 'You can’t disable yourself' : 'Disable account'}
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => setPending({ kind: 'enable', user: u })}
                                >
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  Enable account
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={self}
                                onClick={() => setPending({ kind: 'delete', user: u })}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete user
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {result?.pagination && result.pagination.total > 0 && (
            <Pagination
              className="px-4 pb-4"
              pagination={result.pagination}
              onPageChange={setPage}
              itemLabel="users"
            />
          )}
        </Card>
      </div>

      <InviteUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onCreated={(email, password) => {
          if (password) setSecret({ email, password, created: true });
          else toast({ title: 'User created', description: email });
        }}
      />
      <ImportUsersDialog open={importOpen} onOpenChange={setImportOpen} />

      {pending && confirmProps && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPending(null)}
          {...confirmProps}
          onConfirm={runPending}
        />
      )}

      <SecretDialog
        open={!!secret}
        onClose={() => setSecret(null)}
        title={secret?.created ? 'User created' : 'Temporary password set'}
        description={
          <>
            This password is shown once. Share it with <span className="font-mono">{secret?.email}</span>{' '}
            securely; they should change it after signing in.
          </>
        }
        values={[{ label: 'Temporary password', value: secret?.password ?? '' }]}
        ackLabel="I have copied this password"
      />
    </div>
  );
}

function ImportUsersDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const importUsers = useImportUsers();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<UserImportMode>('skip');
  const [result, setResult] = useState<UserImportResult | null>(null);

  const reset = () => {
    setFile(null);
    setMode('skip');
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!file) return;

    try {
      const csv = await file.text();
      const imported = await importUsers.mutateAsync({ csv, mode });
      setResult(imported);
      toast({
        title: 'Import finished',
        description: `${imported.created} created, ${imported.updated} updated, ${imported.skipped} skipped, ${imported.errors} errors.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Failed to import users',
      });
    }
  };

  const downloadResults = () => {
    if (!result) return;

    const rows = [
      ['row', 'email', 'status', 'message', 'temporary_password'],
      ...result.rows.map((row) => [
        String(row.row),
        row.email,
        row.status,
        row.message || '',
        row.temporaryPassword || '',
      ]),
    ];
    downloadBlob(
      new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }),
      'authrz-users-import-results.csv'
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import Users</DialogTitle>
          <DialogDescription>
            Upload a CSV with email, name, password, status, admin, and optional organization
            columns.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-[1fr_220px]">
            <div className="space-y-2">
              <Label htmlFor="users-csv">CSV file</Label>
              <Input
                id="users-csv"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="space-y-2">
              <Label>Existing users</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as UserImportMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip existing</SelectItem>
                  <SelectItem value="update">Update existing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <ImportStat label="Created" value={result.created} variant="default" />
                <ImportStat label="Updated" value={result.updated} variant="secondary" />
                <ImportStat label="Skipped" value={result.skipped} variant="outline" />
                <ImportStat label="Errors" value={result.errors} variant="destructive" />
              </div>

              <div className="max-h-72 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Temp Password</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((row, index) => (
                      <TableRow key={`${row.row}-${row.email}-${index}`}>
                        <TableCell>{row.row}</TableCell>
                        <TableCell className="font-medium">{row.email || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={row.status === 'error' ? 'destructive' : 'secondary'}>
                            {row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.message || '-'}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {row.temporaryPassword || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {result && (
            <Button variant="outline" onClick={downloadResults}>
              <Download className="h-4 w-4 mr-2" />
              Results CSV
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSubmit} disabled={!file || importUsers.isPending}>
            {importUsers.isPending ? 'Importing...' : 'Import Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportStat({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <Badge variant={variant} className="mt-1">
        {value}
      </Badge>
    </div>
  );
}

function InviteUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (email: string, temporaryPassword?: string | null) => void;
}) {
  const { toast } = useToast();
  const createUser = useCreateUser();
  const { data: organizations = [] } = useOrganizations();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailVerified, setEmailVerified] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>('none');
  const [organizationRole, setOrganizationRole] = useState<(typeof ROLES)[number]>('member');
  const [touched, setTouched] = useState({ name: false, email: false });

  const reset = () => {
    setName('');
    setEmail('');
    setPassword('');
    setEmailVerified(true);
    setIsSuperAdmin(false);
    setOrganizationId('none');
    setOrganizationRole('member');
    setTouched({ name: false, email: false });
  };

  const nameErr = name.trim() ? '' : 'Enter a name.';
  const emailErr = !email.trim()
    ? 'Enter an email address.'
    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ? 'Enter a valid email, like name@company.com.'
      : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true });
    if (nameErr || emailErr) return;
    try {
      const result = await createUser.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        password: password.trim() ? password.trim() : undefined,
        emailVerified,
        isSuperAdmin,
        organizationId: organizationId !== 'none' ? organizationId : undefined,
        organizationRole: organizationId !== 'none' ? organizationRole : undefined,
      });
      reset();
      onOpenChange(false);
      onCreated(result.email, result.temporaryPassword);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not create user',
        description: error instanceof Error ? error.message : 'Failed to create user',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>
              Creates the account. Leave the password blank to generate a temporary one, shown once.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="iu-name">Full name</Label>
              <Input
                id="iu-name"
                autoComplete="off"
                value={name}
                aria-invalid={touched.name && !!nameErr}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setName(e.target.value)}
              />
              {touched.name && nameErr && <p className="text-xs text-destructive">{nameErr}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="iu-email">Email</Label>
              <Input
                id="iu-email"
                type="email"
                autoComplete="off"
                className="font-mono"
                value={email}
                aria-invalid={touched.email && !!emailErr}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setEmail(e.target.value)}
              />
              {touched.email && emailErr && <p className="text-xs text-destructive">{emailErr}</p>}
            </div>
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No organization</SelectItem>
                  {organizations.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Organization role</Label>
              <Select
                value={organizationRole}
                onValueChange={(v) => setOrganizationRole(v as typeof organizationRole)}
                disabled={organizationId === 'none'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="iu-password">
                Password <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="iu-password"
                type="text"
                autoComplete="off"
                className="font-mono"
                placeholder="Leave blank to auto-generate"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Users belong to at most one organization.</p>
            </div>
          </div>

          <div className="grid gap-3 border-t pt-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="iu-verified">Email verified</Label>
                <p className="text-sm text-muted-foreground">
                  Turn off to show the account as unverified until they confirm it.
                </p>
              </div>
              <Switch id="iu-verified" checked={emailVerified} onCheckedChange={setEmailVerified} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="iu-admin">Super admin</Label>
                <p className="text-sm text-muted-foreground">
                  Full control of every organization, app, signing key and the audit log.
                </p>
              </div>
              <Switch id="iu-admin" checked={isSuperAdmin} onCheckedChange={setIsSuperAdmin} />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? <Loader2 className="animate-spin" /> : <UserPlus />}
              {createUser.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function toCsv(rows: string[][]): string {
  return `${rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n')}\r\n`;
}

function escapeCsvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

export type { AdminUser };
